import { Kampos, effects, noise, transitions } from 'kampos';

export class ImageDissolver {
    constructor(elements) {
        this.elements = Array.from(elements);
        this.duration = 800;
        this.isMobile = window.innerWidth <= 1024;
        this.instances = [];
        this.sharedMap = null;
        this.previewLoops = new Map();
        this.init();

        window.addEventListener('resize', this.handleResize.bind(this));
    }

    async init() {
        if (!this.sharedMap) {
            const MAP_WIDTH = 1024;
            const MAP_HEIGHT = 1024;
            const TYPE = 'LIQUID';
            const ANIMATIONS = {
                SMOKE: { octaves: 8, edge: 0.4, cellFactor: 4 },
                LIQUID: { octaves: 1, edge: 0.03, cellFactor: 2 },
            };

            const mapTarget = document.createElement('canvas');
            mapTarget.width = MAP_WIDTH;
            mapTarget.height = MAP_HEIGHT;

            const AMPLITUDE = ANIMATIONS[TYPE].cellFactor / MAP_WIDTH;
            const frequency = { x: AMPLITUDE, y: AMPLITUDE };
            const isFractal = true;

            const turbulence = effects.turbulence({
                noise: noise.simplex,
                frequency,
                isFractal,
            });

            const dissolveMap = new Kampos({
                target: mapTarget,
                effects: [turbulence],
                noSource: true,
            });

            dissolveMap.draw();
            this.sharedMap = mapTarget; 
        }

        for (const el of this.elements) {
            const media1 = el.querySelector('img:not(.alter)');
            const media2 = el.querySelector('.alter');
            if (!media1 || !media2) continue;
            await this.prepareImages([media1, media2]);

            const target = document.createElement('canvas');
            target.width = media1.naturalWidth;
            target.height = media1.naturalHeight;
            el.appendChild(target);

            const dissolve = transitions.dissolve({ high: 0.03 });
            dissolve.map = this.sharedMap; 

            const instance = new Kampos({ target, effects: [dissolve] });

            const width = media1.naturalWidth;
            const height = media1.naturalHeight;

            instance.setSource({ media: media1, width, height });
            dissolve.to = media2;

            const speed = 16 / this.duration;
            let hover = false;
            let playing = false;
            let clicked = false;

            const start = () => {
                if (playing) return;
                playing = true;
                instance.play(function draw() {
                    const targetProgress = hover ? 1 : 0;
                    const current = dissolve.progress || 0;
                    dissolve.progress += (targetProgress - current) * speed;
                });
            };

            const stop = () => {
                if (!instance) return;
                dissolve.progress = 0;
                instance.draw();
                instance.stop();
                playing = false;
            };

            const bindDesktop = () => {
                el.onmouseenter = () => {
                    hover = true;
                    start();
                };
                el.onmouseleave = () => {
                    hover = false;
                    setTimeout(() => {
                        if (!hover) stop();
                    }, this.duration * 2);
                };
            };

            const bindMobile = () => {
                dissolve.progress = 0;
                instance.draw();

                el.onclick = () => {
                    if (clicked) {
                        clicked = false;
                        hover = false;
                        setTimeout(() => {
                            if (!hover) stop();
                        }, this.duration * 2);
                        return;
                    }

                    clicked = true;
                    hover = true;
                    start();
                };
            };

            if (this.isMobile) bindMobile();
            else bindDesktop();

            this.instances.push({
                el,
                instance,
                dissolve,
                bindMobile,
                bindDesktop,
            });
        }
    }

    handleResize() {
        const newIsMobile = window.innerWidth <= 1024;
        if (newIsMobile === this.isMobile) return;

        this.isMobile = newIsMobile;
        this.instances.forEach(({ el, bindMobile, bindDesktop }) => {
            el.onmouseenter = el.onmouseleave = el.onclick = null;
            if (this.isMobile) bindMobile();
            else bindDesktop();
        });
    }

    prepareImages(images) {
        return Promise.all(
            images.map(
                (img) =>
                    new Promise((resolve) => {
                        if (img.complete) return resolve();
                        img.addEventListener('load', resolve, { once: true });
                        img.addEventListener('error', resolve, { once: true });
                    })
            )
        );
    }
}
